import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { cn } from '@/ui/utils/style'

interface ResponsiveDataTableLoadingSkeletonProps {
  desktopRowCount?: number
  mobileRowCount?: number
  showFilterBar?: boolean
  desktopBreakpoint?: 'md' | 'lg'
}

export function ResponsiveDataTableLoadingSkeleton({
  desktopRowCount = 8,
  mobileRowCount = 6,
  showFilterBar = true,
  desktopBreakpoint = 'md',
}: ResponsiveDataTableLoadingSkeletonProps) {
  const desktopContainerClass = desktopBreakpoint === 'lg' ? 'hidden lg:flex' : 'hidden md:flex'
  const mobileContainerClass = desktopBreakpoint === 'lg' ? 'flex lg:hidden' : 'flex md:hidden'

  return (
    <div className="flex flex-col gap-4">
      {showFilterBar && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-20 rounded-full" />
            <Skeleton className="h-9 w-16 rounded-full sm:hidden" />
            <Skeleton className="hidden h-9 w-24 rounded-full sm:block" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="h-9 w-20 rounded-lg" />
          </div>
        </div>
      )}

      <div className={cn('flex-col gap-2', desktopContainerClass)}>
        {Array.from({ length: desktopRowCount }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between rounded-xl border border-white/[0.04] bg-[#111114] p-4"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>

            <div className="flex items-center gap-8">
              <div className="flex flex-col items-end gap-1">
                <Skeleton className="h-4 w-14" />
              </div>
              <div className="flex flex-col items-end gap-1">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-12" />
              </div>
              <div className="flex flex-col items-end gap-1">
                <Skeleton className="h-4 w-12" />
              </div>
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>
        ))}
      </div>

      <div className={cn('flex-col gap-3', mobileContainerClass)}>
        {Array.from({ length: mobileRowCount }).map((_, i) => (
          <div key={i} className="rounded-xl border border-white/[0.06] bg-[#111114] p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
                <div className="flex min-w-0 flex-col gap-1">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-8 w-8 shrink-0 rounded-full" />
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 border-white/5 border-t pt-3">
              <div className="flex flex-col gap-1">
                <Skeleton className="h-2.5 w-8" />
                <Skeleton className="h-4 w-14" />
                <Skeleton className="mt-0.5 h-5 w-12 rounded-full" />
              </div>
              <div className="flex flex-col items-end gap-1">
                <Skeleton className="h-2.5 w-6" />
                <Skeleton className="h-4 w-12" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
